import React, { useRef, useState, useEffect } from 'react';
import styles from './Button.module.scss';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import * as Tone from 'tone';
import hovering from '../../assets/audio/hover.mp3';
import select from '../../assets/audio/click.mp3';

const Button = ({
  link,
  href,
  secondary,
  alternative,
  className,
  children,
  disabled,
  ...props
}) => {
  const [hover, setHover] = useState(false);

  const buttonRef = useRef(null);
  const hoverAudio = useRef(null);
  const clickAudio = useRef(null);

  const playAudio = (audio) => {
    if (audio) {
      Tone.loaded().then(() => {
        audio.start();
      });
    }
  };

  useEffect(() => {
    const button = buttonRef.current;
    hoverAudio.current = new Tone.Player(hovering).toDestination();
    clickAudio.current = new Tone.Player(select).toDestination();

    const hoverStart = () => {
      setHover(true);
    };
    const hoverEnd = () => {
      setHover(false);
    };
    const click = () => {
      if (!disabled) {
        playAudio(clickAudio.current);
      }
    };

    button.addEventListener('pointerenter', hoverStart);
    button.addEventListener('pointerleave', hoverEnd);
    button.addEventListener('pointerdown', click);

    return () => {
      button.removeEventListener('pointerenter', hoverStart);
      button.removeEventListener('pointerleave', hoverEnd);
      button.removeEventListener('pointerdown', click);
    };
  }, [disabled]);

  useEffect(() => {
    if (hover && !disabled) {
      playAudio(hoverAudio.current);
    }
  }, [hover, disabled]);

  const style = classNames(
    styles.button,
    { [styles.secondary]: secondary },
    { [styles.alternative]: alternative },
    className
  );

  if (href) {
    return (
      <a
        ref={buttonRef}
        className={style}
        {...props}
        href={href}
        target={'_blank'}
        rel={'noreferrer'}
        disabled={disabled}
      >
        {children}
      </a>
    );
  }
  if (link) {
    return (
      <Link
        ref={buttonRef}
        className={style}
        to={link}
        disabled={disabled}
        {...props}
      >
        {children}
      </Link>
    );
  } else {
    return (
      <button ref={buttonRef} className={style} disabled={disabled} {...props}>
        {children}
      </button>
    );
  }
};

Button.propTypes = {
  link: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  href: PropTypes.string,
  secondary: PropTypes.bool,
  alternative: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

Button.defaultProps = {
  link: null,
  href: null,
  secondary: false,
  alternative: false,
  disabled: false,
  className: null,
  children: null,
};

export default Button;
